<template>
  <div v-loading="loading">
    <div class="flex items-center justify-between mt-2">
      <page-header
        heading="Account Security"
        breadcrumb="Settings  >>  Account Security"
      />
    </div>
    <div class="bg-white rounded p-4 mt-8 min-h-screen">
      <div class="text-darkBlue mb-12">
        <div class="flex justify-between">
          <div>
            <p class="font-semibold text-lg">Password</p>
            <p class="text-sm">
              The same password strength are enforced for all users across the app.
            </p>
          </div>
          <div>
            <base-button
              text="Update Password"
              background="bg-transparent"
              border="border border-darkBlue"
              @click="showPasswordDivision = !showPasswordDivision"
            />
          </div>
        </div>
        <div v-if="showPasswordDivision" class="mt-5">
          <div class="flex flex-col my-2">
            <label for="current">Current Password</label>
            <base-input
              type="password"
              placeholder="Enter Your Password"
              v-model="lastPassword"
            >
              <template v-slot:icon>
                <svg
                  width="16"
                  height="18"
                  viewBox="0 0 16 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.45118 10.8262V12.7355M1.58551 7.64408V17.1905H15.3169V7.64408H1.58551ZM4.08212 7.64408C4.08212 3.8255 4.08212 1.27979 8.45118 1.27979C12.8202 1.27979 12.8202 3.8255 12.8202 7.64408H4.08212Z"
                    stroke="#FCC210"
                    stroke-width="1.25"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </template>
            </base-input>
          </div>
          <hr />
          <div class="my-2">
            <label for="new">New Password</label>
            <base-input
              type="password"
              placeholder="Enter Your Password"
              v-model="newPassword"
            >
              <template v-slot:icon>
                <svg
                  width="16"
                  height="18"
                  viewBox="0 0 16 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.45118 10.8262V12.7355M1.58551 7.64408V17.1905H15.3169V7.64408H1.58551ZM4.08212 7.64408C4.08212 3.8255 4.08212 1.27979 8.45118 1.27979C12.8202 1.27979 12.8202 3.8255 12.8202 7.64408H4.08212Z"
                    stroke="#FCC210"
                    stroke-width="1.25"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </template>
            </base-input>
          </div>
          <div class="flex flex-col">
            <p class="text-sm text-lightGrey">
              ALL PASSWORDS MUST SATISFY THESE REQUIREMENTS. PASSWORD MUST HAVE:
            </p>
            <div class="text-sm">
              <p
                class="flex items-center py-1"
                :class="{ 'text-red': !hasUpperCase, 'text-green': hasUpperCase }"
              >
                <span v-if="hasUpperCase" class="pr-1">
                  <checkmark />
                </span>
                <span v-else class="pr-1">
                  <img src="@/assets/images/cancel.png" />
                </span>
                Must have at least 1 upper case character
              </p>
              <p
                class="flex items-center py-1"
                :class="{ 'text-red': !hasLowercase, 'text-green': hasLowercase }"
              >
                <span v-if="hasLowercase" class="pr-1">
                  <checkmark />
                </span>
                <span v-else class="pr-1">
                  <img src="@/assets/images/cancel.png" />
                </span>
                Must have at least 1 lower case character
              </p>
              <p
                class="flex items-center py-1"
                :class="{ 'text-red': !hasNumber, 'text-green': hasNumber }"
              >
                <span v-if="hasNumber" class="pr-1">
                  <checkmark />
                </span>
                <span v-else class="pr-1">
                  <img src="@/assets/images/cancel.png" />
                </span>
                Must have at least 1 number
              </p>
              <p
                class="flex items-center py-1"
                :class="{ 'text-red': !hasSpecialChars, 'text-green': hasSpecialChars }"
              >
                <span v-if="hasSpecialChars" class="pr-1">
                  <checkmark />
                </span>
                <span v-else class="pr-1">
                  <img src="@/assets/images/cancel.png" />
                </span>
                Must have at least 1 special character or symbol
              </p>
              <p
                class="flex items-center py-1"
                :class="{ 'text-red': !hasEightChars, 'text-green': hasEightChars }"
              >
                <span v-if="hasEightChars" class="pr-1">
                  <checkmark />
                </span>
                <span v-else class="pr-1">
                  <img src="@/assets/images/cancel.png" />
                </span>
                Must be at least 8 characters
              </p>
            </div>
          </div>
          <div class="my-2">
            <label class="label font-semibold">Confirm Password</label>
            <base-input
              type="password"
              placeholder="Confirm Your Password"
              v-model="confirmPassword"
              errorText="Passwords do not match!"
              :validation="newPassword == confirmPassword"
            >
              <template v-slot:icon>
                <svg
                  width="16"
                  height="18"
                  viewBox="0 0 16 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.45118 10.8262V12.7355M1.58551 7.64408V17.1905H15.3169V7.64408H1.58551ZM4.08212 7.64408C4.08212 3.8255 4.08212 1.27979 8.45118 1.27979C12.8202 1.27979 12.8202 3.8255 12.8202 7.64408H4.08212Z"
                    stroke="#FCC210"
                    stroke-width="1.25"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </template>
            </base-input>
          </div>

          <div class="flex items-center justify-between my-4">
            <base-button
              text="Save"
              width="w-48"
              p="px-12 py-2"
              @click="resetPassword"
              :disabled="!isValidPassword"
              :loading="resettingPassword"
            />
          </div>
        </div>
      </div>

      <div>
        <div class="flex justify-between">
          <div>
            <p class="font-semibold text-lg">Two Factor Authentication</p>
            <p class="text-sm">
              Protect your account with 2-Step Verification.
              <tool-tip
                text="Each time you sign in to your MuleEX Account, you'll need your password and a verification code."
              />
            </p>
          </div>
          <div>
            <base-button
              text="Configure"
              background="bg-transparent"
              border="border border-darkBlue"
              @click="showTwoFaDivision = !showTwoFaDivision"
            />
          </div>
        </div>

        <div
          v-if="showTwoFaDivision"
          class="flex flex-col mt-5 w-75 mb-7"
          v-loading="loading"
        >
          <p class="font-semibold text-lg">
            Protect your account with 2-Step Verification
          </p>
          <p class="w-75 mt-4">
            Each time you sign in to your muleEX Account, you'll need your password and a
            verification code. Learn more
          </p>
          <p class="w-75 mt-4">
            Add an extra layer of security Enter your password and a unique verification
            code that's sent to your phone.
          </p>
          <div class="flex flex-col my-2 mt-5">
            <label for="current">To continue, first verify it’s you</label>
            <div class="flex justify-between gap-4">
              <div class="w-4/5">
                <base-input
                  type="password"
                  placeholder="Enter Your Password"
                  v-model="password"
                >
                  <template v-slot:icon>
                    <svg
                      width="16"
                      height="18"
                      viewBox="0 0 16 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8.45118 10.8262V12.7355M1.58551 7.64408V17.1905H15.3169V7.64408H1.58551ZM4.08212 7.64408C4.08212 3.8255 4.08212 1.27979 8.45118 1.27979C12.8202 1.27979 12.8202 3.8255 12.8202 7.64408H4.08212Z"
                        stroke="#FCC210"
                        stroke-width="1.25"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </template>
                </base-input>
              </div>

              <div class="w-1/5">
                <base-button
                  text="Next"
                  background="bg-transparent"
                  border="border border-darkBlue"
                  :loading="confirmLoading"
                  @click="confirmPasswordTFA"
                />
              </div>
            </div>
          </div>

          <div :class="confirmed ? 'opacity-100' : 'opacity-50'">
            <div class="flex gap-2 my-4">
              <el-switch
                v-model="twoFA"
                active-color="#FCC210"
                inactive-color="#EEEEEE"
                @click="clicked2fa"
                :disabled="!confirmed"
              />
              2FA {{ twoFA ? "Enabled" : "Disabled" }}
            </div>

            <hr class="my-4" />
            <div class="" v-if="tokenSent">
              <h3 class="text-base font-semibold text-primary-yellow mb-3">
                Sent Authentication Code
              </h3>
              <p>
                Enter the one time password sent to your email:
                <span class="text-blue font-semibold"> {{ email }} </span>
              </p>

              <div class="authentication-code-area mx-auto sm:w-full">
                <div class="single-code">
                  <input
                    type="text"
                    class="single-code--input no-outline"
                    maxlength="1"
                    pattern="[a-zA-Z0-9]*"
                    v-model="authenticationCode.first"
                    @keyup="changeInputField"
                    @paste="pasteCode($event)"
                    :disabled="!confirmed"
                  />

                  <input
                    type="text"
                    class="single-code--input no-outline"
                    maxlength="1"
                    pattern="[a-zA-Z0-9]*"
                    v-model="authenticationCode.second"
                    @keyup="changeInputField"
                    :disabled="!confirmed"
                  />
                  <input
                    type="text"
                    class="single-code--input no-outline"
                    maxlength="1"
                    pattern="[a-zA-Z0-9]*"
                    v-model="authenticationCode.third"
                    @keyup="changeInputField"
                    :disabled="!confirmed"
                  />
                  <input
                    type="text"
                    class="single-code--input no-outline"
                    maxlength="1"
                    pattern="[a-zA-Z0-9]*"
                    v-model="authenticationCode.fourth"
                    @keyup="changeInputField"
                    :disabled="!confirmed"
                  />
                  <input
                    type="text"
                    class="single-code--input no-outline"
                    maxlength="1"
                    pattern="[a-zA-Z0-9]*"
                    v-model="authenticationCode.fifth"
                    @keyup="changeInputField"
                    :disabled="!confirmed"
                  />
                  <input
                    type="text"
                    class="single-code--input no-outline"
                    maxlength="1"
                    pattern="[a-zA-Z0-9]*"
                    v-model="authenticationCode.sixth"
                    @keyup="changeInputField"
                    :disabled="!confirmed"
                  />
                </div>
                <div class="verification-status" v-if="verified">
                  <div class="verification successful" v-if="successfullyVerified">
                    <img
                      src="@/assets/images/checkmark.png"
                      alt="checkmark"
                      class="img-fluid"
                    />
                  </div>
                  <div class="verification failed" v-else>
                    <img
                      src="@/assets/images/cancel.png"
                      alt="checkmark"
                      class="img-fluid"
                    />
                  </div>
                </div>
                <base-button
                  text="Submit"
                  @click="enableTwoFA"
                  background="bg-transparent"
                  border="border border-darkBlue"
                  :disabled="!tokenSent"
                  :loading="loading"
                />
              </div>

              <p class="text-primary-yellow my-2 cursor-pointer" @click="sendToken">
                Resend Code
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from "@/components/Ondemand/PageHeader.vue";
import Checkmark from "@/components/Auth/Home/Checkmark";
import changeInputField from "@/mixins/changeInput";

export default {
  components: {
    PageHeader,
    Checkmark,
  },
  data() {
    return {
      bookings: [],
      loading: false,
      updatePassword: false,
      showPasswordDivision: false,
      resettingPassword: false,
      lastPassword: "",
      newPassword: "",
      confirmPassword: "",
      showTwoFaDivision: false,
      confirmLoading: false,
      tokenSent: false,
      confirmed: null,
      twoFA: false,
      password: "",
      authenticationCode: {
        first: "",
        second: "",
        third: "",
        fourth: "",
        fifth: "",
        sixth: "",
      },
      successfullyVerified: false,
      userUpdate: false,
      verified: false,
    };
  },
  mounted() {},
  mixins: [changeInputField],
  watch: {
    twoFA(val) {
      if (this.userUpdate) {
        this.updateTwoFA(val);
      }
    },
  },
  computed: {
    hasEightChars() {
      return this.newPassword.length >= 8 ? true : false;
    },
    hasUpperCase() {
      return /[A-Z]/.test(this.newPassword);
    },
    hasLowercase() {
      return /[a-z]/.test(this.newPassword);
    },
    hasNumber() {
      return /\d/.test(this.newPassword);
    },
    hasSpecialChars() {
      return /[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(this.newPassword);
    },
    isValidPassword() {
      return (
        this.hasEightChars &&
        this.hasUpperCase &&
        this.hasLowercase &&
        this.hasNumber &&
        this.hasSpecialChars &&
        this.newPassword == this.confirmPassword
      );
    },
    userId() {
      return this.$store.state.authentication.userId;
    },
    email() {
      return this.$store.state.authentication.user.email;
    },
    token() {
      return this.$store.state.authentication.quantumToken;
    },
  },
  methods: {
    async confirmPasswordTFA() {
      let requestObj = {
        userId: this.userId,
        password: this.password,
      };
      this.confirmLoading = true;
      try {
        const response = await this.$_confirmPassword(requestObj);
        if (response.data.success) {
          this.confirmed = response.data.success;
          this.getTwoFactorStatus();

          this.confirmLoading = false;
          this.userUpdate = true;
        } else {
          this.generateToast(response.data.error, "error");
          this.confirmLoading = false;
        }
      } catch (error) {
        this.confirmLoading = false;
        this.handleError(error);
      }
    },

    async getTwoFactorStatus() {
      this.loading = true;
      try {
        const response = await this.$_getTwoFactorStatus(this.userId);
        this.loading = false;
        this.twoFA = response.data.data.isSetup;
      } catch (error) {
        this.loading = false;
        this.handleError(error);
      }
    },
    async sendToken() {
      let requestObj = {
        userId: this.userId,
        password: this.password,
      };
      this.loading = true;
      try {
        await this.$_sendToken(requestObj);
        this.tokenSent = true;
        this.loading = false;
        this.generateToast("Token has been resent successfully to your mail", "success");
      } catch (error) {
        this.loading = false;
        this.handleError(error);
      }
    },
    async enableTwoFA() {
      let code = this.convertAuthCodeToNumbers();

      let requestObj = {
        userId: this.userId,
        token: code,
      };
      this.loading = true;
      try {
        await this.$_enableTwoFA(requestObj);
        this.loading = false;
        this.resetState();

        this.generateToast("Two factor Auth enabled successfully", "success");

        this.$emit("close-modal");
      } catch (error) {
        this.loading = false;
        this.handleError(error);
      }
    },
    resetState() {
      this.password = "";
      this.confirmed = null;
      this.tokenSent = false;
      this.authenticationCode = {
        first: "",
        second: "",
        third: "",
        fourth: "",
        fifth: "",
        sixth: "",
      };
    },
    async updateTwoFA(status) {
      let requestObj = {
        userId: this.userId,
        password: this.password,
      };
      this.loading = true;
      try {
        if (status) {
          await this.sendToken(requestObj);
          this.generateToast("Token sent successfully to your email", "success");
        } else {
          await this.$_disableTwoFA(requestObj);
          this.generateToast("2FA disabled", "success");
          this.$emit("close-modal");
        }
        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.handleError(error);
      }
    },
    convertAuthCodeToNumbers() {
      let { first, second, third, fourth, fifth, sixth } = this.authenticationCode;
      let authCodeNumbers = first + second + third + fourth + fifth + sixth;
      return authCodeNumbers;
    },
    pasteCode(e) {
      e.preventDefault();
      let code = e.clipboardData.getData("text/plain");
      console.log("Code:", code);
      if (code.length == 6) {
        this.authenticationCode = {
          first: code.charAt(0),
          second: code.charAt(1),
          third: code.charAt(2),
          fourth: code.charAt(3),
          fifth: code.charAt(4),
          sixth: code.charAt(5),
        };
      } else {
        this.generateToast("Invalid code entered!", "error");
        this.authenticationCode.first = "";
      }
    },
    clicked2fa() {
      this.userUpdate = true;
    },
    createTokenString() {
      // Grabs the query params from the url
      let urlParams = window.location.search;
      // Removes the ? and adds the params as the second item to a new array
      let queryArr = urlParams.split("?");
      console.log(urlParams, "uren");
      // Seprates each param into its own item in a new array
      let queryString = queryArr[1].split("&");
      // splits the first item in the array which holds the token into the value and the paam name
      let tokenArr = queryString[0].split("=");
      // grabs the token value which would be at the second position of the toknArr
      let token = tokenArr[1];
      return token;
    },
    async checkTokenValidity() {
      this.verifyingToken = true;
      let token = this.createTokenString();
      let email = this.$route.query.email;
      let id = this.$route.query.id;
      // console.log(token)
      try {
        let response = await this.$_checkResetPasswordTokenValidity(token, email, id);
        this.log(response);
        this.verifyingToken = false;
        this.isValidToken = true;
      } catch (error) {
        this.handleError(error);
        this.isValidToken = false;
      }
      this.verifyingToken = false;
    },
    async resetPassword() {
      this.resettingPassword = true;
      let requestObject = {
        userId: this.userId,
        previousPassword: this.lastPassword,
        newPassword: this.newPassword,
        confirmPassword: this.confirmPassword,
      };
      try {
        let response = await this.$_resetPassword(requestObject);
        console.log(response);
        this.handleSuccessfulResponse();
      } catch (error) {
        this.resettingPassword = false;
        console.log(error.response);
        this.handleError(error);
      }
    },
    handleSuccessfulResponse() {
      // this.passwordIsReset = true
      this.resettingPassword = false;
      this.generateToast(
        "Your newPassword has been successfully reset! You can now login.",
        "success"
      );
      this.closeModal();
    },
  },
};
</script>

<style>
.button {
  background: #fcc210;
  color: var(--primaryblue);
  width: 40%;
  font-weight: 500;
}

.new-address-button {
  text-decoration-line: underline;

  color: #da9601;
}

.radio-label span::after {
  background: #bec4d5;
}
</style>

<style scoped>
.authentication-screen-wrapper {
  width: 90%;
  margin: 0 auto;
}

.auth-code-form {
  width: 100%;
}

.single-code--input {
  width: 3.5rem;
  height: 3rem;
  background: #ffffff;
  border: 1px solid rgba(82, 82, 82, 0.5);
  box-sizing: border-box;
  border-radius: 8px;
  font-size: 1rem;
  margin-right: 1rem;
}

.single-code--input[type="text"] {
  font-size: 2.3rem;
  text-align: center;
  font-weight: bold;
  color: rgba(82, 82, 82, 0.3);
}

.authentication-code-area {
  display: flex;
  margin-top: 2rem;
}

.verify-btn-container {
  display: flex;
  margin: 2rem 0;
}

.create-password-container {
  margin-top: 3rem;
}

.verification {
  display: flex;
  height: 100%;
  align-items: center;
}

.cursor-pointer {
  cursor: pointer;
}

.lds-ellipsis div {
  background: #0253b3;
}

.loading-area {
  display: flex;
  justify-content: center;
  margin: 3rem 0 0.5rem 0;
}

@media screen and (max-width: 768px) {
  .single-code--input {
    width: 2rem;
    height: 2rem;
  }

  .single-code--input[type="text"] {
    font-size: 1.5rem;
  }

  .authentication-screen-wrapper {
    width: 100%;
    margin: 0 auto;
  }
}
</style>
